'use client';

import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import IntentLink from '@/components/IntentLink';
import { chevronDown as IconChevronDown } from '@/components/icons/chevronDown';
import { chevronUp as IconChevronUp } from '@/components/icons/chevronUp';
import { useQuantaLogin } from '@/hooks/api/quanta/useQuantaLogin';
import { cn } from '@/utils/cn';

import { getChevronColor, getIsQuantaPage, isSidebarItemActive } from '../../utils';

interface SidebarItemProps {
  /** Key within the `layout` message namespace. */
  labelKey: string;
  href: string;
  icon?: React.ReactNode;
  subItems?: SidebarItemProps[];
}

const activeStyle = 'font-bold text-highlight cursor-default';
const inactiveStyle = 'font-medium text-sidebar-inactive hover:text-highlight';

const SidebarItem = ({ labelKey, href, icon, subItems }: SidebarItemProps) => {
  const t = useTranslations('layout');
  const pathname = usePathname();
  const isActive = isSidebarItemActive(pathname, href);
  const isRootActive = pathname === href;

  return (
    <div className="flex flex-col">
      <IntentLink href={href} className={cn('flex items-center justify-between py-4', pathname === href ? activeStyle : inactiveStyle)}>
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center min-w-[26px]">{icon}</div>
          <span>{t(labelKey)}</span>
        </div>
        {subItems &&
          subItems.length > 0 &&
          (isActive ? (
            <IconChevronDown className={getChevronColor(isRootActive)} />
          ) : (
            <IconChevronUp className="text-sidebar-chevron-inactive" />
          ))}
      </IntentLink>
      {subItems && subItems.length > 0 && isActive && (
        <div className="flex flex-col ml-[38px]">
          <SidebarSubItems href={href} subItems={subItems} />
        </div>
      )}
    </div>
  );
};

function SidebarSubItems({ href, subItems }: Pick<SidebarItemProps, 'href'> & { subItems: SidebarItemProps[] }) {
  const t = useTranslations('layout');
  const pathname = usePathname();
  const { data: loginData } = useQuantaLogin({ enabled: false });
  const isNavigationEnabled = !!loginData?.data.user.user_id;
  return (
    <>
      {subItems.map(({ labelKey: subLabelKey, href: subHref }) => {
        const isQuantaSubItem = getIsQuantaPage(href);
        const isDisabled = isQuantaSubItem && !isNavigationEnabled;
        const itemClassName = cn('py-4', pathname === subHref ? activeStyle : inactiveStyle, {
          'text-text-secondary cursor-not-allowed': isDisabled,
        });

        if (isDisabled) {
          return (
            <div key={subHref} className={itemClassName}>
              <span className="leading-18 font-medium">{t(subLabelKey)}</span>
            </div>
          );
        }

        return (
          <IntentLink key={subHref} href={subHref} className={itemClassName}>
            <span className="leading-18 font-medium">{t(subLabelKey)}</span>
          </IntentLink>
        );
      })}
    </>
  );
}

export default SidebarItem;
